import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// Wire schemas for the `care-team-interest` vertical (`/api/v1/care-team-interest`)
// that are safe for the mobile bundle. Member identity is resolved server-side
// from the bearer JWT — no request shape carries a member/identity field
// (ADR-044/045).
//
// The full-row append RESPONSE schema deliberately lives server-side in
// `apps/api/src/care-team-interest/types` (derived via `rowResponse(table)`,
// ADR-066 Rule 3) — NOT here. This module is re-exported from the shared barrel
// the mobile bundle consumes, so it MUST NOT transitively import `../db/*` (that
// would pull the RDS Data API client into the mobile bundle). The mobile record
// tool is fire-and-forget (it discards the echoed row), so the client never needs
// the full-row schema — it validates a minimal ack locally instead.

/**
 * Request body for `POST /api/v1/care-team-interest/responses`. Mirrors the
 * `record_care_team_interest` client-tool params: the binary is the whole
 * signal; `reason` is an optional free-text note (persisted, never event-lifted).
 */
export const recordCareTeamInterestRequestSchema = z
  .object({
    interested: z.boolean(),
    reason: z.string().optional(),
  })
  .strict();
export type RecordCareTeamInterestRequest = z.infer<typeof recordCareTeamInterestRequestSchema>;

/**
 * Response for `GET /api/v1/care-team-interest/booking` — compatibility adapter
 * derived from the member's real upcoming `care_appointments` row. `booked`
 * gates telemedicine join entitlement for older clients; null detail fields mean
 * unbooked.
 */
export const getCareCallBookingDataSchema = z.object({
  booked: z.boolean(),
  providerName: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  appointmentId: z.string().uuid().nullable(),
  durationMinutes: z.number().int().positive().nullable(),
  /** Optional so a new client tolerates an old server that omits the field (ADR-135 Rule 6). */
  joinPromotedFrom: z.string().datetime({ offset: true }).nullable().optional(),
  callType: z.string().nullable().optional(),
});
export type GetCareCallBookingData = z.infer<typeof getCareCallBookingDataSchema>;

export const getCareCallBookingResponseSchema = apiEnvelope(getCareCallBookingDataSchema);
export type GetCareCallBookingResponse = z.infer<typeof getCareCallBookingResponseSchema>;
